import React, { useState, useRef, useEffect } from 'react';
import { Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LAUNCH_DATE = new Date('2026-03-28T00:00:00Z');
const MODELOS = ['Negro Mate', 'Blanco Perla', 'Acero Inoxidable'];

const PRECIOS = {
  'Negro Mate': 79900,
  'Blanco Perla': 86000,
  'Acero Inoxidable': 86000,
};

const ETIQUETAS = {
  'Negro Mate': 'Base',
  'Blanco Perla': 'Edición Limitada',
  'Acero Inoxidable': 'Edición Limitada',
};

const WaitlistSection = ({ onLaunchActive }) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const isLaunched = true;

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="compra"
      ref={sectionRef}
      data-testid="waitlist-section"
      style={{ background: '#F5F5F7', padding: 'clamp(80px,10vw,120px) clamp(24px,6vw,80px)' }}
    >
      {isLaunched ? (
        <CheckoutView visible={visible} />
      ) : (
        <WaitlistForm visible={visible} />
      )}
    </section>
  );
};

/* ─── Waitlist Form ─── */
const WaitlistForm = ({ visible }) => {
  const [form, setForm] = useState({ nombre: '', correo: '', whatsapp: '', ciudad: '', modelo: MODELOS[0] });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [pricingInfo, setPricingInfo] = useState({ units_sold: 0, next_unit: 1, price: 29580, total_units: 999 });

  useEffect(() => {
    const fetchInfo = () => {
      fetch(`${API_URL}/api/payment/info`)
        .then(r => r.clone().json())
        .then(data => setPricingInfo(data))
        .catch(() => {});
    };
    fetchInfo();
    const interval = setInterval(fetchInfo, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nombre || !form.correo || !form.whatsapp || !form.ciudad) {
      setError('Por favor completa todos los campos.');
      return;
    }
    setSubmitting(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      let data;
      try {
        const clone = res.clone();
        data = await clone.json();
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data.detail || 'Error al registrar. Intenta de nuevo.');
      setSuccess(data);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.');
      } else {
        setError(err.message || 'Error al procesar la solicitud. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{
        maxWidth: '600px', margin: '0 auto', textAlign: 'center',
        opacity: 1, animation: 'fadeInUp 700ms ease-out',
      }}>
        <div style={{
          width: '64px', height: '64px', margin: '0 auto 24px',
          background: 'rgba(14,165,233,0.1)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
        }}>
          <Check size={28} color="#0EA5E9" />
        </div>
        <h2 style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 700,
          fontSize: 'clamp(20px, 3vw, 32px)', color: '#0A0A0A',
          marginBottom: '16px', letterSpacing: '-0.02em',
        }}>
          Tu lugar está reservado
        </h2>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '16px', color: '#6B6B6B', lineHeight: 1.6, marginBottom: '8px',
        }}>
          <strong style={{ color: '#0A0A0A' }}>{success.nombre}</strong>, eres el número <strong style={{ color: '#0EA5E9' }}>#{success.numero_en_lista}</strong> en la lista.
        </p>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '15px', color: '#6B6B6B', lineHeight: 1.6,
        }}>
          Modelo de interés: <strong style={{ color: '#0A0A0A' }}>{success.modelo}</strong>
        </p>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '14px', color: '#999', lineHeight: 1.6, marginTop: '24px',
        }}>
          Te notificaremos el 28 de marzo cuando abran las compras.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '560px', margin: '0 auto',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 700ms ease-out, transform 700ms ease-out',
    }}>
      <h2 style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 700,
        fontSize: 'clamp(24px, 3.5vw, 48px)', letterSpacing: '-0.02em',
        color: '#0A0A0A', textAlign: 'center', marginBottom: '12px',
      }}>
        Lista de espera
      </h2>
      <p style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 300,
        fontSize: '16px', color: '#6B6B6B', textAlign: 'center',
        marginBottom: 'clamp(32px, 5vw, 56px)', lineHeight: 1.6,
      }}>
        Reserva tu lugar y sé de los primeros en acceder a ETERNA WATER. Edición limitada a 999 unidades.
      </p>

      {/* Price display */}
      <div style={{ textAlign: 'center', marginBottom: '12px' }}>
        <div data-testid="waitlist-price" style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 700,
          fontSize: 'clamp(32px, 5vw, 56px)', color: '#0A0A0A',
          letterSpacing: '-0.02em', lineHeight: 1.1,
        }}>
          ${pricingInfo.price.toLocaleString('es-MX')} <span style={{ fontWeight: 300, fontSize: 'clamp(14px, 2vw, 18px)', color: '#6B6B6B' }}>MXN</span>
        </div>
        <p data-testid="waitlist-price-label" style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '13px', color: '#6B6B6B', marginTop: '8px',
        }}>
          {pricingInfo.next_unit <= 300 ? 'Precio de lanzamiento — primeras 300 unidades' : 'Precio estándar'}
        </p>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '13px', color: '#6B6B6B', marginTop: '2px',
        }}>
          IVA incluido
        </p>
      </div>

      {/* Reserved counter */}
      <p data-testid="waitlist-counter" style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 300,
        fontSize: '13px', color: '#6B6B6B', textAlign: 'center',
        marginBottom: '32px',
      }}>
        {pricingInfo.units_sold} de {pricingInfo.total_units} lugares reservados
      </p>

      {/* Divider */}
      <div style={{
        width: '100%', height: '1px',
        background: 'rgba(0,0,0,0.08)',
        marginBottom: 'clamp(32px, 5vw, 48px)',
      }} />

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <InputField name="nombre" placeholder="Nombre completo" value={form.nombre} onChange={handleChange} testId="waitlist-nombre" />
        <InputField name="correo" type="email" placeholder="Correo electrónico" value={form.correo} onChange={handleChange} testId="waitlist-correo" />
        <InputField name="whatsapp" type="tel" placeholder="Número de WhatsApp" value={form.whatsapp} onChange={handleChange} testId="waitlist-whatsapp" />
        <InputField name="ciudad" placeholder="Ciudad" value={form.ciudad} onChange={handleChange} testId="waitlist-ciudad" />

        <div>
          <label style={{
            fontFamily: 'Inter, sans-serif', fontWeight: 400,
            fontSize: '12px', letterSpacing: '0.08em', color: '#6B6B6B',
            display: 'block', marginBottom: '8px',
          }}>
            MODELO DE INTERÉS
          </label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {MODELOS.map(m => (
              <button
                key={m}
                type="button"
                data-testid={`modelo-btn-${m.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setForm(f => ({ ...f, modelo: m }))}
                style={{
                  flex: '1 1 auto',
                  padding: '12px 16px',
                  background: form.modelo === m ? 'rgba(14,165,233,0.1)' : 'rgba(255,255,255,0.18)',
                  backdropFilter: 'blur(32px)',
                  WebkitBackdropFilter: 'blur(32px)',
                  border: form.modelo === m ? '1px solid rgba(14,165,233,0.4)' : '1px solid rgba(255,255,255,0.35)',
                  fontFamily: 'Inter, sans-serif', fontWeight: 400,
                  fontSize: '13px', letterSpacing: '0.04em',
                  color: form.modelo === m ? '#0EA5E9' : '#0A0A0A',
                  cursor: 'pointer',
                  transition: 'all 250ms ease',
                }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p data-testid="waitlist-error" style={{
            fontFamily: 'Inter, sans-serif', fontSize: '13px',
            color: '#E53E3E', textAlign: 'center',
          }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="waitlist-submit-btn"
          className="hero-cta ripple-container"
          style={{ width: '100%', marginTop: '8px', opacity: submitting ? 0.6 : 1 }}
        >
          {submitting ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Registrando...
            </span>
          ) : 'Comprar'}
        </button>

        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '13px', color: '#999', textAlign: 'center',
        }}>
          Las compras se habilitan el 28 de marzo de 2026
        </p>
      </form>
    </div>
  );
};

/* ─── Input Field ─── */
const InputField = ({ name, type = 'text', placeholder, value, onChange, testId }) => (
  <input
    name={name}
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    required
    data-testid={testId}
    style={{
      width: '100%',
      padding: '16px 20px',
      background: 'rgba(255,255,255,0.18)',
      backdropFilter: 'blur(32px)',
      WebkitBackdropFilter: 'blur(32px)',
      border: '1px solid rgba(255,255,255,0.35)',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 400,
      fontSize: '15px',
      color: '#0A0A0A',
      outline: 'none',
      transition: 'border 250ms ease',
      boxSizing: 'border-box',
    }}
    onFocus={e => e.target.style.border = '1px solid rgba(14,165,233,0.5)'}
    onBlur={e => e.target.style.border = '1px solid rgba(255,255,255,0.35)'}
  />
);

/* ─── Checkout View (Post-Launch) ─── */
const CheckoutView = ({ visible }) => {
  const [modelo, setModelo] = useState(MODELOS[0]);
  const [correo, setCorreo] = useState('');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/payment/info`)
      .then(r => r.clone().json())
      .then(setInfo)
      .catch(() => {});
  }, []);

  const handleCheckout = async () => {
    if (!correo) { setError('Ingresa tu correo electrónico.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payment/preference`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelo, correo }),
      });
      const clone = res.clone();
      let data;
      try { data = await clone.json(); } catch { data = {}; }
      if (!res.ok) throw new Error(data.detail || 'Error al procesar el pago');
      if (data.init_point) {
        window.location.href = data.init_point;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const price = PRECIOS[modelo];
  const nextUnit = info ? info.next_unit : 1;
  const totalUnits = info ? info.total_units : 999;
  const etiqueta = ETIQUETAS[modelo];

  return (
    <div style={{
      maxWidth: '560px', margin: '0 auto',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(30px)',
      transition: 'opacity 700ms ease-out, transform 700ms ease-out',
    }}>
      <h2 style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 700,
        fontSize: 'clamp(24px, 3.5vw, 48px)', letterSpacing: '-0.02em',
        color: '#0A0A0A', textAlign: 'center', marginBottom: '12px',
      }}>
        Adquiere tu ETERNA
      </h2>
      <p style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: '16px',
        color: '#6B6B6B', textAlign: 'center', marginBottom: '40px',
      }}>
        Unidad #{nextUnit} de {totalUnits} — {etiqueta}
      </p>

      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <span style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 800,
          fontSize: '36px', color: '#0A0A0A', letterSpacing: '-0.02em',
        }}>
          ${price.toLocaleString('es-MX')}
        </span>
        <span style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '16px', color: '#6B6B6B', marginLeft: '8px',
        }}>
          MXN
        </span>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 400,
          fontSize: '12px', letterSpacing: '0.08em', color: '#6B6B6B',
          display: 'block', marginBottom: '8px',
        }}>
          MODELO
        </label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {MODELOS.map(m => (
            <button
              key={m}
              type="button"
              onClick={() => setModelo(m)}
              data-testid={`checkout-modelo-${m.toLowerCase().replace(/\s/g, '-')}`}
              style={{
                flex: '1 1 auto', padding: '12px 16px',
                background: modelo === m ? 'rgba(14,165,233,0.1)' : 'rgba(255,255,255,0.18)',
                backdropFilter: 'blur(32px)',
                border: modelo === m ? '1px solid rgba(14,165,233,0.4)' : '1px solid rgba(255,255,255,0.35)',
                fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px',
                color: modelo === m ? '#0EA5E9' : '#0A0A0A',
                cursor: 'pointer', transition: 'all 250ms ease',
              }}
            >
              {m}
              {ETIQUETAS[m] === 'Edición Limitada' && (
                <span style={{ display: 'block', fontSize: '10px', color: '#999', marginTop: '2px' }}>Ed. Limitada</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <InputField
        name="correo"
        type="email"
        placeholder="Correo electrónico"
        value={correo}
        onChange={e => setCorreo(e.target.value)}
        testId="checkout-correo"
      />

      {error && (
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#E53E3E', textAlign: 'center', marginTop: '12px' }}>
          {error}
        </p>
      )}

      <button
        onClick={handleCheckout}
        disabled={loading}
        data-testid="checkout-pay-btn"
        className="hero-cta ripple-container"
        style={{ width: '100%', marginTop: '20px', opacity: loading ? 0.6 : 1 }}
      >
        {loading ? 'Redirigiendo a Mercado Pago...' : 'Comprar ahora'}
      </button>

      <div style={{
        textAlign: 'center', marginTop: '16px', padding: '16px 0',
        borderTop: '1px solid rgba(0,0,0,0.06)',
      }}>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 500,
          fontSize: '13px', color: '#0EA5E9', marginBottom: '4px',
        }}>
          13% de descuento en la App ETERNA
        </p>
        <p style={{
          fontFamily: 'Inter, sans-serif', fontWeight: 300,
          fontSize: '12px', color: '#999',
        }}>
          Próximamente
        </p>
      </div>

      <p style={{
        fontFamily: 'Inter, sans-serif', fontWeight: 300,
        fontSize: '13px', color: '#999', textAlign: 'center', marginTop: '8px',
      }}>
      </p>
    </div>
  );
};

export default WaitlistSection;
